/**
 * Data Export Utilities
 * Provides CSV and JSON export functionality for analysis results
 */

import { resolveFrameworkType } from '@/lib/benchmarks';
import { getAnalysisPeerComparison } from '@/lib/peer-benchmarks';

/**
 * Export analysis data to CSV format
 * @param {Object} analysisData - The analysis data to export
 */
export function exportToCSV(analysisData) {
  const {
    overall_score,
    category_scores,
    key_strengths,
    priority_improvements,
    company_info,
    recommendations,
  } = analysisData;

  const companyName = company_info?.name || 'Unknown Company';
  const reportYear = company_info?.report_year || '';

  // Build CSV rows
  const rows = [
    ['Modern Slavery Statement Analysis'],
    [''],
    ['Company', companyName],
    ['Report Year', reportYear],
    ['Export Date', new Date().toISOString().split('T')[0]],
    [''],
    ['OVERALL SCORE'],
    ['Overall Compliance Score', overall_score || 0],
    [''],
    ['CATEGORY SCORES'],
  ];

  // Add category scores
  if (category_scores) {
    Object.entries(category_scores).forEach(([category, score]) => {
      rows.push([category, score]);
    });
  }

  rows.push(['']);
  rows.push(['KEY STRENGTHS']);

  if (key_strengths && key_strengths.length > 0) {
    key_strengths.forEach((strength, index) => {
      const text = typeof strength === 'string' ? strength : strength.text || strength.criteria || '';
      rows.push([`${index + 1}`, text]);
    });
  } else {
    rows.push(['None recorded']);
  }

  rows.push(['']);
  rows.push(['PRIORITY IMPROVEMENTS']);
  rows.push(['Area', 'Current Score', 'Priority']);

  if (priority_improvements && priority_improvements.length > 0) {
    priority_improvements.forEach((improvement) => {
      const area = improvement.area || improvement.criteria || String(improvement);
      const score = improvement.score || '';
      const priority = improvement.priority || '';
      rows.push([area, score, priority]);
    });
  } else {
    rows.push(['None recorded', '', '']);
  }

  // Add recommendations if available
  if (recommendations) {
    rows.push(['']);
    rows.push(['STRATEGIC ACTIONS']);

    if (recommendations.immediate && recommendations.immediate.length > 0) {
      rows.push(['']);
      rows.push(['Immediate (0-3 months)']);
      recommendations.immediate.forEach((rec, index) => {
        rows.push([`${index + 1}`, rec]);
      });
    }

    if (recommendations.short_term && recommendations.short_term.length > 0) {
      rows.push(['']);
      rows.push(['Short term (3-6 months)']);
      recommendations.short_term.forEach((rec, index) => {
        rows.push([`${index + 1}`, rec]);
      });
    }

    if (recommendations.long_term && recommendations.long_term.length > 0) {
      rows.push(['']);
      rows.push(['Long term (6-12 months)']);
      recommendations.long_term.forEach((rec, index) => {
        rows.push([`${index + 1}`, rec]);
      });
    }
  }

  // Convert to CSV string
  const csv = rows.map(row =>
    row.map(cell => {
      // Escape quotes and wrap in quotes
      const cellStr = String(cell || '');
      return `"${cellStr.replace(/"/g, '""')}"`;
    }).join(',')
  ).join('\n');

  const filename = `${companyName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-analysis.csv`;
  downloadFile(csv, filename, 'text/csv;charset=utf-8;');
}

/**
 * Export analysis data to JSON format
 * @param {Object} analysisData - The analysis data to export
 */
export function exportToJSON(analysisData) {
  // Create a clean export object
  const exportData = {
    export_info: {
      export_date: new Date().toISOString(),
      export_format: 'json',
      version: '1.0',
    },
    company_info: analysisData.company_info || {},
    overall_score: analysisData.overall_score || 0,
    category_scores: analysisData.category_scores || {},
    key_strengths: analysisData.key_strengths || [],
    priority_improvements: analysisData.priority_improvements || [],
    recommendations: analysisData.recommendations || {},
    framework_type: resolveFrameworkType(analysisData),
    peer_comparison: getAnalysisPeerComparison(analysisData),
    context_summary: analysisData.context_summary || null,
  };

  const json = JSON.stringify(exportData, null, 2);

  const companyName = analysisData.company_info?.name || 'unknown-company';
  const filename = `${companyName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-analysis.json`;

  downloadFile(json, filename, 'application/json');
}

/**
 * Helper function to trigger file download
 * @param {string} content - File content
 * @param {string} filename - Name of the file to download
 * @param {string} mimeType - MIME type of the file
 */
function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // Cleanup
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}
